import React, { useRef, useState } from "react";
import type {
  PricingTableBlock,
  PricingTierItem,
  PricingTierProduct,
} from "@/types/content";
import { resolveHref } from "@/utils/url";
import { getLocale } from "@/utils/locale-store";
import { splitFormattedPrice } from "@/utils/priceFormat";
import { useCarousel } from "@/hooks/useCarousel";
import { responsiveCmsImage } from "@/utils/cmsImage";
import styles from "./PricingTable.module.css";

interface PricingTableProps {
  block: PricingTableBlock;
}

const CheckIcon: React.FC = () => (
  <svg
    className={styles.checkIcon}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M10 0C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0zm-2 15l-5-5 1.41-1.41L8 12.17l7.59-7.59L17 6l-9 9z"
      fill="currentColor"
    />
  </svg>
);

interface TierCtaProps {
  tier: PricingTierItem;
  selectedProduct: PricingTierProduct | null;
}

const TierCta: React.FC<TierCtaProps> = ({ tier, selectedProduct }) => {
  const className = [
    styles.ctaButton,
    tier.isHighlighted ? styles.ctaHighlighted : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (tier.buttonLink) {
    const href = resolveHref(tier.buttonLink, getLocale().language);
    const target = tier.buttonLink.target || undefined;
    return (
      <a
        href={href}
        className={className}
        target={target}
        rel={target === "_blank" ? "noopener noreferrer" : undefined}
      >
        {tier.buttonText}
      </a>
    );
  }

  if (selectedProduct) {
    return (
      <a href={selectedProduct.href} className={className}>
        {tier.buttonText}
      </a>
    );
  }

  return <span className={className}>{tier.buttonText}</span>;
};

interface PriceDisplayProps {
  price: string;
  suffix?: string | null;
}

const PriceDisplay: React.FC<PriceDisplayProps> = ({ price, suffix }) => {
  const parts = splitFormattedPrice(price);
  // Symbols sit flush against the amount ("85⁹⁹€", the tier-card design), but a
  // multi-letter name needs the locale's space back or it reads as "1 199⁰⁰kr".
  const currencyGap = parts && parts.currency.length > 1 ? " " : "";
  return (
    <div className={styles.priceRow}>
      <span className={styles.price}>
        {parts ? (
          <>
            {parts.currencyFirst && `${parts.currency}${currencyGap}`}
            {parts.whole}
            <span className={styles.priceDecimals}>{parts.decimals}</span>
            {!parts.currencyFirst && `${currencyGap}${parts.currency}`}
          </>
        ) : (
          price
        )}
      </span>
      {suffix && <span className={styles.priceSuffix}>{suffix}</span>}
    </div>
  );
};

interface ProductSelectorFieldProps {
  products: PricingTierProduct[];
  value: number;
  onChange: (index: number) => void;
  inputId: string;
}

const ProductSelectorField: React.FC<ProductSelectorFieldProps> = ({
  products,
  value,
  onChange,
  inputId,
}) => (
  <div className={styles.quantityField}>
    <select
      id={inputId}
      className={styles.quantitySelect}
      value={String(value)}
      onChange={(e) => onChange(Number.parseInt(e.target.value, 10))}
    >
      {products.map((product, index) => (
        <option key={index} value={String(index)}>
          {product.label ?? `#${index + 1}`}
        </option>
      ))}
    </select>
  </div>
);

interface TierCardBodyProps {
  tier: PricingTierItem;
  showHeader?: boolean;
}

const TierCardBody: React.FC<TierCardBodyProps> = ({
  tier,
  showHeader = true,
}) => {
  const products = tier.products ?? [];
  const hasProducts = products.length >= 1;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const safeIndex = Math.min(selectedIndex, Math.max(products.length - 1, 0));
  const selectedProduct = hasProducts ? (products[safeIndex] ?? null) : null;

  const displayedPrice = selectedProduct?.formattedPrice ?? tier.price ?? null;

  return (
    <>
      <div className={styles.tierContent}>
        {showHeader && (
          <>
            <h3 className={styles.tierName}>{tier.tierName}</h3>
            {tier.title && <p className={styles.tierTitle}>{tier.title}</p>}
          </>
        )}
        <div className={styles.divider} />
        {displayedPrice && (
          <PriceDisplay price={displayedPrice} suffix={tier.priceSuffix} />
        )}
        {products.length >= 2 && (
          <ProductSelectorField
            inputId={`tier-${tier.id}-product`}
            products={products}
            value={safeIndex}
            onChange={setSelectedIndex}
          />
        )}
        <div className={styles.divider} />
        <div className={styles.features}>
          {tier.featuresPrefix && (
            <p className={styles.featuresPrefix}>{tier.featuresPrefix}</p>
          )}
          <ul className={styles.featureList} role="list">
            {tier.features.map((feature, index) => (
              <li key={index} className={styles.featureItem}>
                <CheckIcon />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className={styles.tierFooter}>
        <TierCta tier={tier} selectedProduct={selectedProduct} />
      </div>
    </>
  );
};

interface TierCardProps {
  tier: PricingTierItem;
}

const TierCard: React.FC<TierCardProps> = ({ tier }) => {
  const cardClassName = [
    styles.tierCard,
    tier.isHighlighted ? styles.highlighted : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <article className={cardClassName}>
      <TierCardBody tier={tier} />
    </article>
  );
};

interface MobileTierCarouselProps {
  tiers: PricingTierItem[];
}

const SWIPE_THRESHOLD_PX = 50;

const MobileTierCarousel: React.FC<MobileTierCarouselProps> = ({ tiers }) => {
  const { currentIndex, goTo, goPrev, goNext, translateX } = useCarousel({
    itemCount: tiers.length,
    loop: false,
  });
  const touchStartX = useRef<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const endX = e.changedTouches[0]?.clientX ?? touchStartX.current;
    const delta = endX - touchStartX.current;
    touchStartX.current = null;
    if (delta > SWIPE_THRESHOLD_PX) goPrev();
    else if (delta < -SWIPE_THRESHOLD_PX) goNext();
  };

  const handleTabKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const lastIndex = tiers.length - 1;
    let nextIndex: number | null = null;
    switch (e.key) {
      case "ArrowLeft":
        nextIndex = currentIndex === 0 ? lastIndex : currentIndex - 1;
        break;
      case "ArrowRight":
        nextIndex = currentIndex === lastIndex ? 0 : currentIndex + 1;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = lastIndex;
        break;
      case "Enter":
      case " ":
        nextIndex = currentIndex;
        break;
      default:
        return;
    }
    e.preventDefault();
    goTo(nextIndex);
    const tabs =
      e.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>(
        '[role="tab"]'
      );
    tabs?.[nextIndex]?.focus();
  };

  return (
    <div className={styles.mobileCarousel}>
      <article className={styles.tierCard}>
        <div
          className={styles.topViewport}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div
            className={styles.topTrack}
            style={{ transform: `translateX(${translateX})` }}
          >
            {tiers.map((tier) => (
              <div key={tier.id} className={styles.topSlide}>
                {tier.image && (
                  <div className={styles.tierImage}>
                    <img
                      // Only the mobile carousel (768px and down) shows tier
                      // images, edge to edge across the card.
                      {...responsiveCmsImage(
                        tier.image.url,
                        "100vw",
                        tier.image.width
                      )}
                      alt={tier.image.altText ?? ""}
                      width={tier.image.width || undefined}
                      height={tier.image.height || undefined}
                      loading="lazy"
                    />
                  </div>
                )}
                {tier.title && (
                  <p className={styles.mobileTierTitle}>{tier.title}</p>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className={styles.tierSelector} role="tablist">
          {tiers.map((tier, index) => {
            const isActive = index === currentIndex;
            return (
              <button
                key={tier.id}
                type="button"
                role="tab"
                id={`tab-${tier.id}`}
                aria-controls={`panel-${tier.id}`}
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                className={[
                  styles.tierSelectorButton,
                  isActive ? styles.tierSelectorActive : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => goTo(index)}
                onKeyDown={handleTabKeyDown}
              >
                {tier.tierName}
              </button>
            );
          })}
        </div>
        <div
          className={styles.carouselViewport}
          role="region"
          aria-roledescription="carousel"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <div
            className={styles.carouselTrack}
            style={{ transform: `translateX(${translateX})` }}
          >
            {tiers.map((tier, index) => {
              const isActive = index === currentIndex;
              return (
                <div
                  key={tier.id}
                  className={styles.carouselSlide}
                  role="tabpanel"
                  id={`panel-${tier.id}`}
                  aria-labelledby={`tab-${tier.id}`}
                  aria-hidden={!isActive}
                  tabIndex={isActive ? 0 : -1}
                >
                  <TierCardBody tier={tier} showHeader={false} />
                </div>
              );
            })}
          </div>
        </div>
      </article>
    </div>
  );
};

export const PricingTable: React.FC<PricingTableProps> = ({ block }) => {
  const { heading, subtitle, pricingTiers, isMainTitle = false } = block;

  if (!pricingTiers || pricingTiers.length === 0) {
    return null;
  }

  // The editor can mark this heading as the page h1; the styling stays the same.
  const HeadingTag = isMainTitle ? "h1" : "h2";

  return (
    <section className={styles.container}>
      {(heading || subtitle) && (
        <div className={styles.header}>
          {heading && (
            <HeadingTag className={styles.heading}>{heading}</HeadingTag>
          )}
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>
      )}
      <div className={styles.tiersGrid} data-count={pricingTiers.length}>
        {pricingTiers.map((tier) => (
          <TierCard key={tier.id} tier={tier} />
        ))}
      </div>
      <MobileTierCarousel tiers={pricingTiers} />
    </section>
  );
};

export default PricingTable;
